import CleanInvitationDownload from "../CleanInvitationDownload";
import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../../firebase";
import { callEventAutopilot } from "../../Utils/eventAutopilotApi";
import { callRsvpCommerce, submitCheckoutRequest } from "../../Utils/rsvpCommerceApi";
import PackageWorkflowCards, { workflowStepParts } from "./PackageWorkflowCards";

export { workflowStepIcon, workflowStepParts, workflowStepText } from "./PackageWorkflowCards";

const money = (minor, currency) => new Intl.NumberFormat("he-IL", { style: "currency", currency }).format(Number(minor) / 100);

const BADGE_LABELS = { RECOMMENDED: "מומלץ", POPULAR: "פופולרי" };

const CheckoutProgress=()=> <span className="autopilot-commerce__payment-progress"><span className="autopilot-voice__button-spinner" aria-hidden="true" />מעביר לתשלום…</span>;

const PackageCard = ({ item, disabled, onOpen }) => {
  const badge = BADGE_LABELS[item.badge];
  const lowestTier = item.tiers.reduce((lowest, tier) => (!lowest || tier.priceMinor < lowest.priceMinor ? tier : lowest), null);
  const summarySteps = item.steps.slice(0, 3);
  const remainingSteps = Math.max(0, item.steps.length - summarySteps.length);

  return (
    <article className="autopilot-commerce__package-card" dir="rtl">
      <span className="autopilot-commerce__card-glow" aria-hidden="true" />
      <header className="autopilot-commerce__package-header">
        <div className="autopilot-commerce__package-topline">
          <span className="autopilot-commerce__eyebrow">שליחת הודעות אוטומטיות</span>
          {badge && <span className="autopilot-commerce__badge">{badge}</span>}
        </div>
        <h3>{item.customerFacingName}</h3>
        {item.shortDescription && <p>{item.shortDescription}</p>}
      </header>

      <section className="autopilot-commerce__summary" aria-label={`עיקרי חבילת ${item.customerFacingName}`}>
        <h4>עיקרי החבילה</h4>
        <ul>{summarySteps.map((step) => <li key={step.id}>{workflowStepParts(step).action}</li>)}</ul>
        {remainingSteps > 0 && <small>ועוד {remainingSteps} שלבים אוטומטיים בחבילה</small>}
      </section>

      <footer className="autopilot-commerce__card-footer autopilot-commerce__card-footer--summary">
        {lowestTier && <div className={`autopilot-commerce__starting-price ${lowestTier.manualDiscountMinor > 0 ? "autopilot-commerce__starting-price--discounted" : ""}`}>
          {lowestTier.manualDiscountMinor > 0 ? (
            <>
              <small>מחיר מלא <del>{money(lowestTier.fullPriceMinor, item.currency)}</del></small>
              <small>הנחת MANUAL: {money(lowestTier.manualDiscountMinor, item.currency)}</small>
              <strong>{money(lowestTier.priceMinor, item.currency)}</strong>
            </>
          ) : <><small>החל מ־</small><strong>{money(lowestTier.priceMinor, item.currency)}</strong></>}
        </div>}
        <button className="autopilot-commerce__purchase" type="button" disabled={disabled} onClick={() => onOpen(item)}>לפרטים ורכישה</button>
      </footer>
    </article>
  );
};

const ManualCard = ({ item, disabled, busyKey, onPurchase, watermarkEnabled }) => {
  const clean=item.packageKind === "CLEAN_INVITATION";
  const title=clean ? item.customerFacingName : "אישורי הגעה ללא הגבלה";
  const key = `${item.packageId}:fixed`;
  return (
    <article className={"autopilot-commerce__package-card autopilot-commerce__package-card--manual"+(clean ? " autopilot-commerce__package-card--clean" : " autopilot-commerce__package-card--bestseller")} dir="rtl">
      <header className="autopilot-commerce__package-header">
        <div className="autopilot-commerce__package-topline">
          <span className={`autopilot-commerce__eyebrow${!clean ? " autopilot-commerce__eyebrow--limit" : ""}`}>
            {clean ? "הזמנה נקייה" : "פתיחת המערכת ללא הגבלת אנשי קשר"}
          </span>
          {!clean ? <span className="autopilot-commerce__badge autopilot-commerce__badge--bestseller">הכי נמכר</span> : BADGE_LABELS[item.badge] && <span className="autopilot-commerce__badge">{BADGE_LABELS[item.badge]}</span>}
        </div>
        <h3>{title}</h3>
        {item.shortDescription && <p>{item.shortDescription}</p>}
      </header>
      <section className="autopilot-commerce__summary" aria-label={`עיקרי מסלול ${title}`}>
        <h4>מה כולל המסלול</h4>
        <ul>
          {clean ? <><li>הורדת סימן המים מההזמנה</li><li>לינק ישיר להורדה למחשב/טלפון</li><li>קישור חדש להזמנה לשליחה בוואטסאפ ופלטפורמות הדיגיטליות ללא סימן המים.</li></> : <><li>צבירת אישורי הגעה ללא הגבלה</li><li>אתם שולחים בעצמכם את הלינק מהווטאסאפ שלכם.</li>{watermarkEnabled && <li>הורדת סימן המים מההזמנה</li>}</>}
        </ul>
      </section>
      <footer className="autopilot-commerce__card-footer autopilot-commerce__card-footer--summary">
        <div className="autopilot-commerce__starting-price"><small>מחיר</small><strong>{money(item.fixedPriceMinor, item.currency)}</strong></div>
        <button className="autopilot-commerce__purchase" type="button" disabled={disabled || Boolean(busyKey)} aria-busy={busyKey === key} onClick={() => onPurchase(item)}>
          {busyKey === key ? <CheckoutProgress /> : `המשך לתשלום – ${money(item.fixedPriceMinor, item.currency)}`}
        </button>
      </footer>
    </article>
  );
};

const PackageDetailsModal = ({ item, disabled, busyKey, onClose, onPreview, onPurchase }) => {
  const defaultTier = item.tiers.find((tier) => tier.upToRecords === 100) || item.tiers[0];
  const [selectedCeiling, setSelectedCeiling] = useState(defaultTier?.upToRecords || "");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(true);
  const [scheduleUnavailable, setScheduleUnavailable] = useState(false);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const closeTimer = useRef(null);
  const selectedTier = item.tiers.find((tier) => tier.upToRecords === Number(selectedCeiling)) || defaultTier;
  const key = selectedTier ? `${item.packageId}:${selectedTier.upToRecords}` : "";

  useEffect(() => {
    let active = true;
    setPreviewLoading(true); setPreviewError(""); setPreview(null); setScheduleUnavailable(false);
    onPreview(item)
      .then((result) => { if (active) setPreview(result.plan); })
      .catch((error) => {
        if (!active) return;
        if (error?.code === "AUTOPILOT_PACKAGE_SCHEDULE_UNAVAILABLE") {
          setScheduleUnavailable(true);
          setPreviewError("החבילה אינה זמינה במסגרת לוחות הזמנים של האירוע שלכם.");
          return;
        }
        setPreviewError("תאריך האירוע לא מאפשר להשתמש בחבילה הזאת - יש לבחור חבילה אחרת.");
      })
      .finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; };
  }, [item, onPreview]);

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    closeTimer.current = window.setTimeout(onClose, reducedMotion ? 0 : 220);
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => { if (event.key === "Escape") requestClose(); };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(closeTimer.current);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [requestClose]);

  const previewSteps = preview?.steps || item.steps;

  return (
    <div className={`autopilot-commerce__modal-backdrop ${closing ? "is-closing" : "is-opening"}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}>
      <section className={`autopilot-commerce__modal ${closing ? "is-closing" : "is-opening"}`} role="dialog" aria-modal="true" aria-labelledby="commerce-package-modal-title" dir="rtl">
        <button className="autopilot-commerce__modal-close" type="button" aria-label="סגירת פרטי החבילה" onClick={requestClose}>×</button>
        <header className="autopilot-commerce__modal-header">
          <h2 id="commerce-package-modal-title">{item.customerFacingName}</h2>
          {item.shortDescription && <p>{item.shortDescription}</p>}
        </header>

        <PackageWorkflowCards
          steps={previewSteps}
          headingId="commerce-package-workflow-title"
          loading={previewLoading}
          error={previewError}
        />

        <footer className="autopilot-commerce__modal-purchase">
          <div className="autopilot-commerce__tier-control">
            <label htmlFor={`commerce-modal-tier-${item.packageId}`}>בחרו מספר רשומות - כל רשומה היא בית אב אחד לשליחת הזמנה.</label>
            <select
              id={`commerce-modal-tier-${item.packageId}`}
              value={selectedCeiling}
              disabled={disabled || Boolean(busyKey)}
              onChange={(event) => setSelectedCeiling(Number(event.target.value))}
            >
              {item.tiers.map((tier) => <option key={tier.upToRecords} value={tier.upToRecords}>עד {tier.upToRecords} רשומות</option>)}
            </select>
          </div>
          {selectedTier && <div className="autopilot-commerce__price" aria-live="polite">
            {selectedTier.manualDiscountMinor > 0 ? (
              <>
                <small>מחיר מלא <del>{money(selectedTier.fullPriceMinor, item.currency)}</del></small>
                <small>הנחת MANUAL: {money(selectedTier.manualDiscountMinor, item.currency)}</small>
                <strong>{money(selectedTier.priceMinor, item.currency)}</strong>
              </>
            ) : <><small>מחיר החבילה</small><strong>{money(selectedTier.priceMinor, item.currency)}</strong></>}
          </div>}
          <button
            className="autopilot-commerce__purchase"
            type="button"
            aria-busy={busyKey === key}
            data-schedule-unavailable={scheduleUnavailable ? "true" : undefined}
            disabled={disabled || Boolean(busyKey) || !selectedTier || previewLoading || Boolean(previewError)}
            onClick={() => selectedTier && onPurchase(item.packageId, selectedTier)}
          >
            {busyKey === key ? <CheckoutProgress /> : `המשך לתשלום – ${money(selectedTier?.priceMinor, item.currency)}`}
          </button>
        </footer>
      </section>
    </div>
  );
};

const CommercePackagePicker = ({ eventId, disabled, freeLimitReached = false }) => {
  const [catalog, setCatalog] = useState([]);
  const [watermarkEnabled,setWatermarkEnabled]=useState(false);
  const [cleanDownloadUrl,setCleanDownloadUrl]=useState("");
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);

  const ownerToken = useCallback(async () => {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) throw Object.assign(new Error("Authentication required"), { code: "RSVP_COMMERCE_AUTH_REQUIRED" });
    return idToken;
  }, []);

  const call = useCallback(async (action, payload = {}) => (
    callRsvpCommerce({ idToken: await ownerToken(), eventId, action, payload })
  ), [eventId, ownerToken]);

  const previewPackage = useCallback(async (item) => (
    callEventAutopilot({
      idToken: await ownerToken(),
      eventId,
      action: "previewActivation",
      payload: { packageId: item.packageId, packageRevision: item.packageRevision },
    })
  ), [eventId, ownerToken]);

  const loadCatalog = useCallback(async () => {
    setLoading(true); setError("");
    try {const result=await call("listCatalog");setWatermarkEnabled(result.watermarkEnabled===true);setCleanDownloadUrl(result.cleanDownloadUrl||"");setCatalog((result.packages||[]).filter(item=>item.packageKind!=="CLEAN_INVITATION" || (result.watermarkEnabled===true && !result.cleanDownloadUrl)).sort((a,b)=>(a.packageKind==="CLEAN_INVITATION"?-1:0)-(b.packageKind==="CLEAN_INVITATION"?-1:0))); }
    catch (_error) { setError("לא ניתן לטעון כרגע את החבילות. אפשר לנסות שוב ללא שינוי באירוע."); }
    finally { setLoading(false); }
  }, [call]);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  const purchase = async (item, tier = null) => {
    const key = `${item.packageId}:${tier ? tier.upToRecords : "fixed"}`; setBusyKey(key); setError("");
    try {
      const result = await call("createOffer", {
        packageId: item.packageId,
        ...(tier ? { upToRecords: tier.upToRecords } : {}),
      });
      submitCheckoutRequest(result.checkoutRequest);
    } catch (requestError) {
      setBusyKey("");
      setError(requestError.code === "RSVP_COMMERCE_SALES_DISABLED"
        ? "הרכישה עדיין אינה פתוחה. האירוע הקיים לא השתנה."
        : "לא ניתן לפתוח את התשלום כרגע. לא חויבתם ואפשר לנסות שוב.");
    }
  };

  const displayCatalog = freeLimitReached
    ? [...catalog.filter(item => item.packageKind === "MANUAL"), ...catalog.filter(item => item.packageKind !== "MANUAL")]
    : catalog;

  return (
    <section className="autopilot-card autopilot-commerce" aria-labelledby="autopilot-commerce-title">
      <header className="autopilot-commerce__sales-header">
        <img
          className="autopilot-commerce__sales-header-icon"
          src="https://ucarecdn.com/eb358cc7-c732-4e38-92e3-c416cc1b3a1a/noroot.png"
          alt=""
        />
        <div className="autopilot-commerce__sales-header-copy">
          <h2 id="autopilot-commerce-title">בחרו את המסלול שמתאים לאירוע שלכם</h2>
          <p>אפשר לנהל את אישורי ההגעה ידנית או לבחור חבילת הודעות לשליחה אוטומטית</p>
        </div>
      </header>
      {loading && (
        <p className="autopilot-commerce__loading" role="status" aria-live="polite">
          <span className="autopilot-commerce__loading-spinner" aria-hidden="true" />
          טוען חבילות…
        </p>
      )}
      {!loading && catalog.length === 0 && <p>אין כרגע חבילות זמינות לרכישה.</p>}
      {cleanDownloadUrl && (
        <div className="autopilot-commerce__clean-download">
          <strong>ההזמנה ללא סימן מים זמינה להורדה</strong>
          <CleanInvitationDownload url={cleanDownloadUrl} className="autopilot-commerce__purchase" />
        </div>
      )}
      <div className="autopilot-commerce__packages">
        {displayCatalog.map((item) => ["MANUAL","CLEAN_INVITATION"].includes(item.packageKind)
          ? <ManualCard key={`${item.packageId}:${item.packageRevision}`} item={item} disabled={disabled} busyKey={busyKey} onPurchase={purchase} watermarkEnabled={watermarkEnabled} />
          : <PackageCard key={`${item.packageId}:${item.packageRevision}`} item={item} disabled={disabled} onOpen={setSelectedPackage} />)}
      </div>
      {selectedPackage && <PackageDetailsModal key={`${selectedPackage.packageId}:${selectedPackage.packageRevision}`} item={selectedPackage} disabled={disabled} busyKey={busyKey} onClose={() => setSelectedPackage(null)} onPreview={previewPackage} onPurchase={(packageId, tier) => purchase(selectedPackage, tier)} />}
      {error && <div className="autopilot-commerce__error" role="alert"><p>{error}</p><button type="button" onClick={loadCatalog}>ניסיון נוסף</button></div>}
    </section>
  );
};

export default CommercePackagePicker;
